// First-touch marketing attribution (UTM tags, ad click ids, external referrer).
// The landing page (arabcap.co) and the editor (app.arabcap.co) share it through
// a cookie on .arabcap.co, mirrored in localStorage; it is sent with signup so the
// API can tell where the user came from. Keep in sync with
// LandingPage/src/attribution.ts.

const KEY = 'ac_attr';
const MAX_AGE = 60 * 60 * 24 * 180; // 180 days
const MAX_LEN = 300;
const PARAMS = [
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
  'gclid', 'gbraid', 'wbraid', 'fbclid', 'ttclid', 'msclkid', 'ref',
];
const SITE_DOMAIN = 'arabcap.co';
// Auth and payment redirects are not marketing sources.
const IGNORED_REFERRERS = ['accounts.google.com', 'checkout.stripe.com', 'billing.stripe.com'];

const cookieDomain = () => {
  const host = window.location.hostname;
  return host === SITE_DOMAIN || host.endsWith(`.${SITE_DOMAIN}`) ? `; Domain=.${SITE_DOMAIN}` : '';
};

const isInternal = (host) => host === window.location.hostname
  || host === SITE_DOMAIN
  || host.endsWith(`.${SITE_DOMAIN}`)
  || IGNORED_REFERRERS.includes(host);

const hasSignal = (touch) => PARAMS.some((p) => touch[p]) || Boolean(touch.referrer);

const read = () => {
  try {
    const match = document.cookie.match(new RegExp(`(?:^|; )${KEY}=([^;]*)`));
    if (match) return JSON.parse(decodeURIComponent(match[1]));
  } catch { /* unreadable cookie */ }
  try {
    const stored = localStorage.getItem(KEY);
    if (stored) return JSON.parse(stored);
  } catch { /* storage blocked */ }
  return null;
};

const write = (touch) => {
  const json = JSON.stringify(touch);
  try {
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${KEY}=${encodeURIComponent(json)}; Max-Age=${MAX_AGE}; Path=/; SameSite=Lax${cookieDomain()}${secure}`;
  } catch { /* cookies blocked */ }
  try { localStorage.setItem(KEY, json); } catch { /* storage blocked */ }
};

// Record this visit. The first touch is kept, except that a visit with a real
// source (UTM, click id, external referrer) replaces an earlier direct visit.
export const captureAttribution = () => {
  try {
    const touch = {};
    const params = new URLSearchParams(window.location.search);
    PARAMS.forEach((p) => {
      const value = params.get(p);
      if (value) touch[p] = value.slice(0, MAX_LEN);
    });
    if (document.referrer) {
      try {
        if (!isInternal(new URL(document.referrer).hostname)) touch.referrer = document.referrer.slice(0, MAX_LEN);
      } catch { /* malformed referrer */ }
    }
    touch.landing_page = `${window.location.origin}${window.location.pathname}`.slice(0, MAX_LEN);
    touch.first_seen_at = new Date().toISOString();

    const existing = read();
    if (!existing || (hasSignal(touch) && !hasSignal(existing))) write(touch);
  } catch { /* never break the page over analytics */ }
};

export const getAttribution = () => read();
